import { defineQuery } from 'bitecs';
import { AI, AI_STATE, Position, Animal, Plant, PLANT_TYPES, Time, Production, Sprite, Structure } from '../world.js';
import { createBee } from '../entities/bee.js';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, CHUNK_SIZE, CURRENT_MODE } from '../config.js';
import { TICKS_PER_HOUR } from '../systems/time.js';

// Reference to app for animations
let app = null;

// Export setter function for app reference
export function setApp(pixiApp) {
    app = pixiApp;
}

const beeQuery = defineQuery([AI, Position, Animal]);
const flowerQuery = defineQuery([Plant, Position]);
const hiveQuery = defineQuery([Structure, Position, Production]);

const FLOWER_SEARCH_RADIUS = 400;
const FLOWER_GATHER_RADIUS = 15;
const GATHERING_TIME = 100;
const HIVE_RETURN_RADIUS = 32;
export const BEE_CARRYING_CAPACITY = 5;
const NECTAR_GATHER_RATE = 1;
const DAILY_HONEY_QUOTA = 50;

// Bee activity time constants (in hours)
const BEE_ACTIVITY_START = 8;
const BEE_ACTIVITY_PEAK = 12;
const BEE_ACTIVITY_END = 17;
const BEE_RETURN_START = 16;

// Entity-specific storage for bee data
const beeData = new Map();
const hiveData = new Map();

// Make beeData accessible to other systems
export function getBeeData(beeId) {
    return beeData.get(beeId) || { nectarCarried: 0, hiveId: null };
}

// Function to get hive data
function getHiveData(hiveId) {
    if (!hiveData.has(hiveId)) {
        hiveData.set(hiveId, {
            dailyHoneyProduced: 0,
            lastResetDay: 0,
            activeBees: 0,
            maxBees: 5,  // Reduced from 10 to 5 for better balance
            lastBeeSpawnTime: 0,
            beeSpawnInterval: 5000  // Spawn new bee every 5 seconds if needed
        });
        console.log(`[BEE] Initialized hive #${hiveId} with max ${hiveData.get(hiveId).maxBees} bees`);
    }
    return hiveData.get(hiveId);
}

// Function to update bee count for a hive
function updateHiveBeeCount(world, hiveId) {
    const data = getHiveData(hiveId);
    let activeBees = 0;
    
    // Count active bees for this hive
    for (const bee of beeQuery(world)) {
        if (Animal.type[bee] === 4 && AI.hiveEntity[bee] === hiveId) {
            activeBees++;
        }
    }
    
    // Only log if the count has changed
    if (data.activeBees !== activeBees) {
        console.log(`[BEE] Hive #${hiveId} bee count updated: ${activeBees}/${data.maxBees} bees`);
    }
    
    data.activeBees = activeBees;
    return activeBees;
}

// Function to spawn a new bee for a hive
function spawnNewBeeForHive(world, hiveId) {
    const hive = world.entities.find(e => e === hiveId);
    if (!hive) return null;
    
    const hiveData = getHiveData(hiveId);
    if (hiveData.activeBees >= hiveData.maxBees) {
        console.log(`[BEE] Hive #${hiveId} at max capacity (${hiveData.activeBees}/${hiveData.maxBees} bees)`);
        return null;
    }
    
    const hiveX = Position.x[hive];
    const hiveY = Position.y[hive];
    
    // Spawn bee in a random position around the hive
    const angle = Math.random() * Math.PI * 2;
    const distance = 32; // 32 units away from hive
    const beeX = hiveX + Math.cos(angle) * distance;
    const beeY = hiveY + Math.sin(angle) * distance;
    
    const bee = createBee(world, beeX, beeY, hiveId);
    if (bee) {
        console.log(`[BEE] Spawned new bee #${bee} for hive #${hiveId} at (${beeX.toFixed(1)}, ${beeY.toFixed(1)})`);
        return bee;
    }
    return null;
}

// Function to check if hive has reached daily quota
function hasHiveReachedQuota(hiveId, currentDay) {
    const data = getHiveData(hiveId);
    
    // Reset daily production if it's a new day
    if (data.lastResetDay !== currentDay) {
        data.dailyHoneyProduced = 0;
        data.lastResetDay = currentDay;
    }
    
    return data.dailyHoneyProduced >= DAILY_HONEY_QUOTA;
}

// Function to deposit nectar at hive
function depositNectarAtHive(world, hive, amount) {
    if (!hive || !Production.amount[hive]) return false;
    
    const currentDay = Math.floor(Time.ticks[world.timeEntity] / (TICKS_PER_HOUR * 24));
    const hiveId = hive;
    
    // Check if hive has reached daily quota
    if (hasHiveReachedQuota(hiveId, currentDay)) {
        console.log(`[BEE] Hive #${hiveId} has reached daily honey quota`);
        return false;
    }
    
    // Convert nectar to honey (1:1 ratio for now)
    const honeyAmount = amount;
    const hiveData = getHiveData(hiveId);
    
    // Update hive's honey production
    Production.amount[hive] = Math.min(
        Production.amount[hive] + honeyAmount,
        Production.maxAmount[hive]
    );
    
    // Update daily production tracking
    hiveData.dailyHoneyProduced += honeyAmount;
    
    console.log(`[BEE] Deposited ${honeyAmount} honey at hive #${hiveId}. Total: ${Production.amount[hive]}, Daily: ${hiveData.dailyHoneyProduced}/${DAILY_HONEY_QUOTA}`);
    return true;
}

// Function to check if bees should be active based on time
function shouldBeesBeActive(currentTicks) {
    const currentHour = currentTicks / TICKS_PER_HOUR;
    
    // If it's before activity start or after activity end, bees should be inactive
    if (currentHour < BEE_ACTIVITY_START || currentHour > BEE_ACTIVITY_END) {
        return false;
    }
    
    // If it's between return start and activity end, bees should start returning
    if (currentHour >= BEE_RETURN_START) {
        return false;
    }
    
    return true;
}

// Function to set random wander target with directional bias
function setRandomWanderTarget(world, bee, beeX, beeY) {
    const maxWanderDistance = TILE_SIZE * 150; // Increased from 100 to 150 tiles
    const minWanderDistance = TILE_SIZE * 30;  // Increased from 20 to 30 tiles
    
    // Get the bee's data to track exploration
    const data = beeData.get(bee);
    if (!data.lastWanderDirection) {
        data.lastWanderDirection = Math.random() * Math.PI * 2;
    }
    
    // Bias the angle based on last direction, but allow some randomness
    const angleVariation = Math.PI / 4; // 45 degrees variation
    const baseAngle = data.lastWanderDirection;
    const randomVariation = (Math.random() - 0.5) * angleVariation;
    const newAngle = baseAngle + randomVariation;
    
    // Update last direction for next time
    data.lastWanderDirection = newAngle;
    
    // Calculate distance with some randomness
    const distance = minWanderDistance + Math.random() * (maxWanderDistance - minWanderDistance);
        
    // Calculate new target position
    const targetX = beeX + Math.cos(newAngle) * distance;
    const targetY = beeY + Math.sin(newAngle) * distance;
    
    // Ensure target is within map bounds
    const boundedX = Math.max(TILE_SIZE, Math.min(targetX, MAP_WIDTH * TILE_SIZE - TILE_SIZE));
    const boundedY = Math.max(TILE_SIZE, Math.min(targetY, MAP_HEIGHT * TILE_SIZE - TILE_SIZE));
    
    console.log(`[BEE] Setting wander target to (${Math.round(boundedX)}, ${Math.round(boundedY)}) with angle ${Math.round(newAngle * 180 / Math.PI)}°`);
    
    AI.targetX[bee] = boundedX;
    AI.targetY[bee] = boundedY;
}

// Function to find nearest flower with nectar
function findNearestFlower(world, beeX, beeY) {
    const flowers = flowerQuery(world);
    let nearestFlower = null;
    let minDistanceSq = Infinity;

    const beeChunkX = Math.floor(beeX / (TILE_SIZE * CHUNK_SIZE));
    const beeChunkY = Math.floor(beeY / (TILE_SIZE * CHUNK_SIZE));
    const chunkSearchRadius = 2; // Search 2 chunks out (5x5 grid)
    const minChunkX = beeChunkX - chunkSearchRadius;
    const maxChunkX = beeChunkX + chunkSearchRadius;
    const minChunkY = beeChunkY - chunkSearchRadius;
    const maxChunkY = beeChunkY + chunkSearchRadius;

    let flowersInNearbyChunks = 0;

    // console.log(`[BEE] Searching for flowers near (${Math.round(beeX)}, ${Math.round(beeY)}). Total flowers: ${flowers.length}. Chunk: ${beeChunkX},${beeChunkY}`);

    for (const flower of flowers) {
        const flowerX = Position.x[flower];
        const flowerY = Position.y[flower];

        // --- Chunk Check --- 
        const flowerChunkX = Math.floor(flowerX / (TILE_SIZE * CHUNK_SIZE));
        const flowerChunkY = Math.floor(flowerY / (TILE_SIZE * CHUNK_SIZE));
        
        if (flowerChunkX < minChunkX || flowerChunkX > maxChunkX || flowerChunkY < minChunkY || flowerChunkY > maxChunkY) {
            continue; // Skip flower if not in nearby chunks
        }
        flowersInNearbyChunks++; // Count flowers checked after chunk culling

        // --- DETAILED LOGGING FOR NEARBY FLOWERS ---
        const isFlowerType = Plant.type[flower] === PLANT_TYPES.FLOWER.id;
        const isMature = Plant.growth[flower] >= 0.7; // Changed from < 0.7 to >= 0.7
        const hasNectar = Plant.foodAmount[flower] > 0;
        // console.log(`  - Checking flower #${flower}: TypeOK=${isFlowerType}, MatureOK=${isMature} (Growth: ${Plant.growth[flower]?.toFixed(2)}), NectarOK=${hasNectar} (Nectar: ${Plant.foodAmount[flower]})`);
        // --- END LOGGING --- 

        // --- Original Checks (Type, Growth, Nectar) --- 
        if (!isFlowerType || !isMature || !hasNectar) { // Use the checked variables
            continue;
        }
        
        // --- Distance Check (only for flowers in nearby chunks) ---
        const dx = flowerX - beeX;
        const dy = flowerY - beeY;
        const distSq = dx * dx + dy * dy;
        
        // Check against FLOWER_SEARCH_RADIUS (squared)
        if (distSq < FLOWER_SEARCH_RADIUS * FLOWER_SEARCH_RADIUS && distSq < minDistanceSq) {
            minDistanceSq = distSq;
            nearestFlower = flower;
            // console.log(`[BEE] Found potential flower #${flower} at (${Math.round(flowerX)}, ${Math.round(flowerY)}) with ${Math.round(Plant.foodAmount[flower])} nectar`);
        }
    }
    
    // Updated log message
    console.log(`[BEE] Searching flowers near (${Math.round(beeX)},${Math.round(beeY)}). Checked ${flowersInNearbyChunks} flowers in nearby chunks. Found: ${nearestFlower !== null}`);

    return nearestFlower;
}

// Function to return to hive
function returnToHive(world, bee, beeX, beeY, hiveX, hiveY) {
    AI.targetX[bee] = hiveX;
    AI.targetY[bee] = hiveY;
    AI.state[bee] = AI_STATE.MOVING;
}

export function beeSystem(world, delta) {
    // Exit early if bees are disabled
    if (!CURRENT_MODE.bees) {
        // console.log("[BeeSys] Skipping bee system (disabled in CURRENT_MODE)");
        return world;
    }

    // Ensure delta is a valid number
    const safeDelta = typeof delta === 'number' && !isNaN(delta) ? delta : 16.67;
    const bees = beeQuery(world);
    
    // Get time entity to check time of day
    const timeEntity = world.timeEntity;
    const currentTicks = timeEntity ? Time.ticks[timeEntity] : 0;
    const isActive = shouldBeesBeActive(currentTicks);

    // Update bee counts for all hives and spawn new bees if needed
    const hives = hiveQuery(world);
    for (const hiveId of hives) {
        const hiveData = getHiveData(hiveId);
        const activeBees = updateHiveBeeCount(world, hiveId);
        
        // Check if we need to spawn more bees AND if hives/bees are enabled
        if (CURRENT_MODE.hives && CURRENT_MODE.bees && activeBees < hiveData.maxBees) {
            const currentTime = Date.now();
            if (currentTime - hiveData.lastBeeSpawnTime >= hiveData.beeSpawnInterval) {
                const newBee = spawnNewBeeForHive(world, hiveId);
                if (newBee) {
                    hiveData.lastBeeSpawnTime = currentTime;
                }
            }
        }
    }

    // Clean up any dead bees
    for (const bee of bees) {
        if (!world.entities.includes(bee)) {
            console.log(`[BEE] Cleaning up dead bee ${bee}`);
            // Remove components
            if (Position[bee]) delete Position[bee];
            if (Animal[bee]) delete Animal[bee];
            if (AI[bee]) delete AI[bee];
            if (Sprite[bee]) delete Sprite[bee];
            // Remove from beeData
            beeData.delete(bee);
            // Remove from ECS world
            if (world.removeEntity) world.removeEntity(bee);
        }
    }

    for (const bee of bees) {
        if (Animal.type[bee] !== 4) continue; // Skip if not a bee

        const beeX = Position.x[bee];
        const beeY = Position.y[bee];
        const hiveEntity = AI.hiveEntity[bee];

        // Initialize bee data if needed
        if (!beeData.has(bee)) {
            beeData.set(bee, {
                directionChangeTimer: 0,
                directionChangeInterval: 2000 + Math.random() * 3000, // 2-5 seconds
                targetFlower: null,
                gatheringTimer: 0,
                nectarCarried: 0,
                hiveId: hiveEntity, // Set the hive ID when initializing bee data
                lastWanderDirection: null
            });
        }
        
        const data = beeData.get(bee);
        // Ensure hiveId is set even for existing bees
        if (!data.hiveId) {
            data.hiveId = hiveEntity;
        }
        data.directionChangeTimer += safeDelta; // Use safeDelta here too

        // Find the bee's hive
        const hive = world.entities.find(e => e === hiveEntity);
        if (!hive) continue;

        const hiveX = Position.x[hive];
        const hiveY = Position.y[hive];

        // Calculate distance to hive
        const dx = hiveX - beeX;
        const dy = hiveY - beeY;
        const distanceToHive = Math.sqrt(dx * dx + dy * dy);

        // Handle different states
        switch (AI.state[bee]) {
            case AI_STATE.EATING:
                // If bee is full, return to hive immediately
                if (data.nectarCarried >= BEE_CARRYING_CAPACITY) {
                    console.log(`[BEE] Full of nectar (${data.nectarCarried}/${BEE_CARRYING_CAPACITY}), returning to hive`);
                    AI.state[bee] = AI_STATE.MOVING;
                    data.targetFlower = null;
                    returnToHive(world, bee, beeX, beeY, hiveX, hiveY);
                    break;
                }

                // If no target flower or flower is empty, look for next flower
                if (!data.targetFlower || Plant.foodAmount[data.targetFlower] <= 0) {
                    console.log(`[BEE DEBUG] Current flower empty, looking for next flower...`);
                    AI.state[bee] = AI_STATE.MOVING;
                    const oldFlower = data.targetFlower;
                    data.targetFlower = null;
                    
                    // If it's time to return to hive, do so
                    if (!isActive) {
                        returnToHive(world, bee, beeX, beeY, hiveX, hiveY);
                    } else {
                        // Search for next nearest flower
                        const nextFlower = findNearestFlower(world, beeX, beeY);
                        if (nextFlower && nextFlower !== oldFlower) {
                            console.log(`[BEE] Found next flower #${nextFlower} with ${Plant.foodAmount[nextFlower]} nectar`);
                            data.targetFlower = nextFlower;
                            const flowerX = Position.x[nextFlower];
                            const flowerY = Position.y[nextFlower];
                            AI.targetX[bee] = flowerX;
                            AI.targetY[bee] = flowerY;
                        } else {
                            console.log(`[BEE] No more flowers found, wandering randomly`);
                            setRandomWanderTarget(world, bee, beeX, beeY);
                        }
                    }
                    break;
                    }
                    
                // Debug: Log flower state
                if (data.targetFlower) {
                    const flowerX = Position.x[data.targetFlower];
                    const flowerY = Position.y[data.targetFlower];
                    const dx = flowerX - beeX;
                    const dy = flowerY - beeY;
                    const distSq = dx * dx + dy * dy;
                    console.log(`[BEE DEBUG] Flower #${data.targetFlower} at (${Math.round(flowerX)}, ${Math.round(flowerY)})`);
                    console.log(`[BEE DEBUG] Distance to flower: ${Math.round(Math.sqrt(distSq))}`);
                    console.log(`[BEE DEBUG] Flower nectar: ${Plant.foodAmount[data.targetFlower]}`);
                }

                // Gather nectar from flower
                data.gatheringTimer += safeDelta;
                console.log(`[BEE DEBUG] Gathering timer: ${Math.round(data.gatheringTimer)}/${GATHERING_TIME}`);
                
                // Check if finished gathering
                if (data.gatheringTimer >= GATHERING_TIME) {
                    console.log(`[BEE DEBUG] Attempting to gather nectar...`);
                    // Take nectar from flower if available
                    if (data.targetFlower && Plant.foodAmount[data.targetFlower] > 0) {
                        const nectarToTake = Math.min(
                            NECTAR_GATHER_RATE,
                            Plant.foodAmount[data.targetFlower],
                            BEE_CARRYING_CAPACITY - data.nectarCarried
                        );
                        
                        console.log(`[BEE DEBUG] Can take ${nectarToTake} nectar (Rate: ${NECTAR_GATHER_RATE}, Flower has: ${Plant.foodAmount[data.targetFlower]}, Bee can carry: ${BEE_CARRYING_CAPACITY - data.nectarCarried})`);
                        
                        if (nectarToTake > 0) {
                            Plant.foodAmount[data.targetFlower] -= nectarToTake;
                            data.nectarCarried += nectarToTake;
                            console.log(`[BEE DEBUG] Successfully gathered ${nectarToTake} nectar. Now carrying ${data.nectarCarried}/${BEE_CARRYING_CAPACITY}`);
                            
                            // If bee is now full, return to hive immediately
                            if (data.nectarCarried >= BEE_CARRYING_CAPACITY) {
                                console.log(`[BEE] Full of nectar (${data.nectarCarried}/${BEE_CARRYING_CAPACITY}), returning to hive`);
                                AI.state[bee] = AI_STATE.MOVING;
                                data.targetFlower = null;
                                returnToHive(world, bee, beeX, beeY, hiveX, hiveY);
                                break;
                            }
                        } else {
                            console.log(`[BEE DEBUG] Could not take nectar - all conditions failed`);
                    }
                } else {
                        console.log(`[BEE DEBUG] No flower or flower has no nectar`);
                    }
                    
                    // Reset timer for next gathering cycle
                    data.gatheringTimer = 0;
                }
                break;

            case AI_STATE.MOVING:
                // If bee is full, return to hive immediately
                if (data.nectarCarried >= BEE_CARRYING_CAPACITY) {
                    console.log(`[BEE] Full of nectar (${data.nectarCarried}/${BEE_CARRYING_CAPACITY}), returning to hive`);
                    returnToHive(world, bee, beeX, beeY, hiveX, hiveY);
                    break;
                }

                // If at hive, deposit nectar
                if (distanceToHive <= HIVE_RETURN_RADIUS && data.nectarCarried > 0) {
                    if (depositNectarAtHive(world, hive, data.nectarCarried)) {
                        data.nectarCarried = 0;
                        console.log(`[BEE] Successfully deposited nectar at hive #${hive}`);
                        
                        // If hive hasn't reached quota, go back to searching
                        const currentDay = Math.floor(Time.ticks[world.timeEntity] / (TICKS_PER_HOUR * 24));
                        if (!hasHiveReachedQuota(hive, currentDay)) {
                            setRandomWanderTarget(world, bee, beeX, beeY);
                        } else {
                            // Stay at hive if quota reached
                            AI.targetX[bee] = hiveX;
                            AI.targetY[bee] = hiveY;
                    }
                    } else {
                        // If deposit failed (quota reached), stay at hive
                        AI.targetX[bee] = hiveX;
                        AI.targetY[bee] = hiveY;
                    }
                }
                
                // If it's not active time, return to hive
                if (!isActive) {
                    if (distanceToHive > HIVE_RETURN_RADIUS) {
                        returnToHive(world, bee, beeX, beeY, hiveX, hiveY);
                    } else {
                        // At hive, stay still
                        AI.targetX[bee] = beeX;
                        AI.targetY[bee] = beeY;
                    }
                } else {
                    // Active time behavior
                    // Look for flowers while moving
                    if (!data.targetFlower || data.directionChangeTimer >= data.directionChangeInterval) {
                        data.targetFlower = findNearestFlower(world, beeX, beeY);
                        data.directionChangeTimer = 0;
                        
                        if (!data.targetFlower) {
                            console.log(`[BEE] No flowers found, wandering randomly`);
                            setRandomWanderTarget(world, bee, beeX, beeY);
                        }
                    }
                    
                    // If we found a flower, move towards it
                    if (data.targetFlower && Position.x[data.targetFlower] !== undefined) {
                    const flowerX = Position.x[data.targetFlower];
                    const flowerY = Position.y[data.targetFlower];
                    const flowerDx = flowerX - beeX;
                    const flowerDy = flowerY - beeY;
                    const distanceToFlower = Math.sqrt(flowerDx * flowerDx + flowerDy * flowerDy);
                    
                    if (distanceToFlower < FLOWER_GATHER_RADIUS) {
                            console.log(`[BEE] Reached flower #${data.targetFlower} at distance ${Math.round(distanceToFlower)}`);
                        AI.state[bee] = AI_STATE.EATING;
                        data.gatheringTimer = 0;
                        } else {
                            AI.targetX[bee] = flowerX;
                            AI.targetY[bee] = flowerY;
                        }
                    } else {
                        // No flower found, continue wandering
                        if (data.directionChangeTimer >= data.directionChangeInterval || 
                            !AI.targetX[bee] || !AI.targetY[bee] ||
                            (Math.abs(AI.targetX[bee] - beeX) < 1 && Math.abs(AI.targetY[bee] - beeY) < 1)) {
                            setRandomWanderTarget(world, bee, beeX, beeY);
                            data.directionChangeTimer = 0;
                        }
                    }
                }
                break;
                
            default:
                // Reset to moving state if in unknown state
                AI.state[bee] = AI_STATE.MOVING;
                break;
        }

        // Move towards target if not eating
        if (AI.state[bee] !== AI_STATE.EATING) {
            const targetX = AI.targetX[bee];
            const targetY = AI.targetY[bee];
            const moveX = targetX - beeX;
            const moveY = targetY - beeY;
            const moveDist = Math.sqrt(moveX * moveX + moveY * moveY);

            if (moveDist > 0) {
                const secondsElapsed = safeDelta / 1000;
                const moveSpeed = TILE_SIZE * 2 * secondsElapsed;
                const effectiveSpeed = Math.min(moveSpeed, TILE_SIZE * 0.5);
                
                const moveXAmount = (moveX / moveDist) * effectiveSpeed;
                const moveYAmount = (moveY / moveDist) * effectiveSpeed;
                
                Position.x[bee] = beeX + moveXAmount;
                Position.y[bee] = beeY + moveYAmount;

        // Update sprite position
        if (world.sprites[bee]) {
            world.sprites[bee].position.set(Position.x[bee], Position.y[bee]);
            
                    // Flip sprite based on movement direction
                    if (moveX !== 0) {
                        world.sprites[bee].scale.x = Math.abs(world.sprites[bee].scale.x) * (moveX > 0 ? 1 : -1);
                    }
                    
                    // Update sprite color based on state
                    if (data.nectarCarried > 0) {
                        // More golden tint based on nectar amount
                        const tintAmount = Math.min(1, data.nectarCarried / BEE_CARRYING_CAPACITY);
                        const baseColor = 0xFFFFFF;
                        const nectarColor = 0xFFD700;
                        const r = Math.floor(((nectarColor >> 16) & 0xFF) * tintAmount + ((baseColor >> 16) & 0xFF) * (1 - tintAmount));
                        const g = Math.floor(((nectarColor >> 8) & 0xFF) * tintAmount + ((baseColor >> 8) & 0xFF) * (1 - tintAmount));
                        const b = Math.floor((nectarColor & 0xFF) * tintAmount + (baseColor & 0xFF) * (1 - tintAmount));
                        world.sprites[bee].tint = (r << 16) | (g << 8) | b;
            } else if (AI.state[bee] === AI_STATE.EATING) {
                        world.sprites[bee].tint = 0x90EE90; // Light green when gathering
                    } else if (!isActive) {
                        world.sprites[bee].tint = 0xADD8E6; // Light blue when returning to hive
            } else {
                        world.sprites[bee].tint = 0xFFFFFF; // White when searching
                    }
            }
            }
        }
    }

    // Clean up data for deleted bees
    for (const beeId of beeData.keys()) {
        // Check if the entity still exists in the main query AND is actually a bee
        if (!bees.includes(beeId) || (Animal.type && Animal.type[beeId] !== 4)) { 
            console.log(`[BEE] Cleaning up data for non-existent or non-bee entity ${beeId}`);
            beeData.delete(beeId);
            // Note: Entity removal from ECS world should happen elsewhere (e.g., when energy <= 0)
        }
    }
} 